import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { MainLayout } from './components/layout/MainLayout';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { ApiKeysGuidePage } from './pages/ApiKeysGuidePage';
import { DashboardPage } from './pages/DashboardPage';
import { BudgetAnalyticsPage } from './pages/BudgetAnalyticsPage';
import { AdminSettingsPage } from './pages/AdminSettingsPage';
import { BudgetsPage } from './pages/BudgetsPage';
import { BudgetDetailPage } from './pages/BudgetDetailPage';
import { NewBudgetPage } from './pages/NewBudgetPage';
import { EditBudgetPage } from './pages/EditBudgetPage';
import { BudgetConflictPage } from './pages/BudgetConflictPage';
import { TransactionsPage } from './pages/TransactionsPage';
import { TransactionDetailPage } from './pages/TransactionDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { InvitationsPage } from './pages/InvitationsPage';
import { LockScreen } from './pages/LockScreen';
import { FaceIdEnrollSheet } from './components/security/FaceIdEnrollSheet';
import { LoadingSpinner } from './components/ui/Loading';
import { Toaster } from '@/components/ui/Toast';
import { isStandaloneDisplay } from '@/lib/pwa';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isLocked, isFaceIdEnrolled } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isLocked && isFaceIdEnrolled && isStandaloneDisplay()) {
    return <LockScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { isAuthenticated, isLoading, isLocked, isFaceIdEnrolled } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Enrolled PWA lock takes over the whole app shell
  if (isLocked && isFaceIdEnrolled && isStandaloneDisplay()) {
    return (
      <>
        <LockScreen />
        <Toaster />
      </>
    );
  }

  return (
    <>
      <Routes>
        {/* Landing page for unauthenticated users */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        
        {/* Login page (from Cognito redirect) */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} />

        {/* Public docs — reachable signed-in or out */}
        <Route path="/guide/api-keys" element={<ApiKeysGuidePage />} />
        <Route path="/docs/api-keys" element={<Navigate to="/guide/api-keys" replace />} />
        <Route path="/guide/shortcuts" element={<Navigate to="/guide/api-keys" replace />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
        </Route>

        <Route
          path="/budgets"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<BudgetsPage />} />
          <Route path="new" element={<NewBudgetPage />} />
          <Route path=":id" element={<BudgetDetailPage />} />
          <Route path=":id/edit" element={<EditBudgetPage />} />
          <Route path=":id/edit/conflict" element={<BudgetConflictPage />} />
        </Route>

        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<BudgetAnalyticsPage />} />
        </Route>

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminSettingsPage />} />
        </Route>

        <Route
          path="/transactions"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TransactionsPage />} />
          <Route path=":id" element={<TransactionDetailPage />} />
        </Route>

        <Route
          path="/invitations"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<InvitationsPage />} />
        </Route>

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SettingsPage />} />
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <FaceIdEnrollSheet />
      <Toaster />
    </>
  );
}
